import { createPortal } from "react-dom";
import { showToast } from "../../components/toastService.jsx";
import { OverflowButton, useListOverflow } from "../components/ListOverflow.jsx";
// src/v2/pulse/InFocusRadarPage.jsx
//
// In Focus Radar — full-ledger view of all portfolio-tracked items,
// engine-ranked by urgency. Seed-backed this slice; real object emitters
// wired in a future task.
//
// DEMO ONLY — triage actions (Pin/Snooze/Mute) and Refresh are local state only.

import { Fragment, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SlideSheet } from "../../components/SlideSheet";
import { restoreInFocusCard, getPulseLocationRisk } from "../../data/pulseClient.js";
import { getFocusItemsFeed, dismissFocusItem, snoozeFocusItem, unsnoozeFocusItem, resolveFocusItem, unresolveFocusItem, muteFocusItem, unmuteFocusItem, getMutedFocusItems } from "../../data/focusItemsClient.js";
import { DismissedGroup, DismissedCardRow } from "./PulsePage.jsx";
import { Pin, Clock, BellOff, MoreHorizontal, ExternalLink, ChevronDown, Trash2 } from "lucide-react";
import { LAYERS, T } from "../../app/tokens.js";
import {
  useFeatureFlagContext,
  useFeatureFlags,
  FEATURE_SCORING_MODEL_ARCHIVED,
} from "../../contexts/FeatureFlagContext.jsx";
import { homeNavEnabled } from "./homeNav.js";
import { buildLeaseUrl, buildLocationUrl, buildMarketPlanUrl } from "../../services/buildUrl.js";
import { matchesFoldedQuery } from "../../utils/textSearch.js";
import { PAGE_CONTENT_MAX_WIDTH, PAGE_CONTENT_PADDING } from "../../components/PageHeaderStack.jsx";
import { KpiTile } from "../../components/KpiTile.jsx";
import { KpiTileRow } from "../../features/plan/KpiTileRow.jsx";
import { SearchInput } from "../components/SearchInput.jsx";
import { SortControl } from "../components/SortControl.jsx";
import { CompactSwitch } from "../components/ViewControls.jsx";
import { useUiPrefs } from "../hooks/useUiPrefs.js";
import { RadarDecisionsPanel } from "../radar/RadarDecisionsPanel.jsx";

const RADAR_DECISIONS_FLAG = "FEATURE_RADAR_DECISIONS_REGISTER";
import { UI_PREFS_INFOCUS_RADAR_V1 } from "../../services/portfolioStatePersistence.js";

/* ─── API adapter ────────────────────────────────────────────────────────── */
// Maps the server's feed shape → the UI item model used throughout this file.
// Extra fields included in the API response (e.g. in test mocks) are forwarded
// as-is so the adapter is transparent to enriched data.

const TYPE_MAP_API = {
  decision: "Decision",
  action:   "Action",
  signal:   "Watch",
  expiry:   "Issue",
  issue:    "Issue",
  project:  "Watch",
};

function computeDueFromDate(dueDate) {
  if (!dueDate) return null;
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const d = new Date(`${dueDate}T00:00:00Z`);
  const days = Math.round((d - today) / 86_400_000);
  const absLabel = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return {
    label:     days < 0 ? "Overdue" : days === 0 ? "Today" : absLabel,
    countdown: `${days}d`,
  };
}

function adaptApiItem(api) {
  const uiType    = api.uiType ?? TYPE_MAP_API[api.type] ?? "Watch";
  const sourceObj = (api.subject_type === "lease" || api.type === "expiry")
                  ? "Lease"
                  : "Office";
  const handoffSubject = api.playbook_context?.subject ?? api.playbookContext?.subject ?? null;

  // Stable UI id: focus_items uses dedupe_key/id; legacy feed used itemKey.
  const rawId = api.dedupe_key ?? api.id ?? api.itemKey;

  // Due date: focus_items stores due_at (ISO timestamp); legacy feed used dueDate.
  const dueDateStr = api.due_at
    ? api.due_at.slice(0, 10)
    : (api.dueDate ?? null);

  // Urgency: pass-through when present; derive from focus_items score (0–100 cap).
  const urgency = api.urgency !== undefined && api.urgency !== null
    ? api.urgency
    : api.score != null
      ? Math.min(100, Math.round(api.score))
      : null;

  // inFocus: pass-through when present; derive from band for focus_items rows.
  // P-57b: review-type items banded to this_cycle are NOT in-focus — only
  // reviews that are materially overdue (act_now) are elevated.
  const inFocus = api.inFocus !== undefined
    ? api.inFocus
    : api.type === "review"
      ? api.band === "act_now"
      : (api.band === "act_now" || api.band === "this_cycle");

  // Owner: focus_items stores a string in owner_id; legacy feed used owner object/string.
  const owner = api.owner_id
    ? { name: String(api.owner_id) }
    : api.owner
      ? (typeof api.owner === "object" ? api.owner : { name: String(api.owner) })
      : null;

  return {
    id:      rawId,
    _dbId:   api.id ?? rawId,   // DB UUID for lifecycle API calls (dismiss/resolve)
    rawType: api.type ?? null,  // Raw DB-level type for per-type primary actions
    type:    uiType,
    band:  api.band ?? null,
    source: api.source ?? {
      object:  sourceObj,
      // Prefer server-resolved location_id (focus_items rows with subject_type=office/market)
      // over legacy locationId (radar feed rows from radarClient.js).
      // Use null (not rawId/dedupe_key) when neither is present so the "Open location"
      // drawer button suppresses cleanly instead of routing to an invalid URL.
      id:      api.location_id ?? api.locationId ?? null,
      label:   api.locationName ?? handoffSubject?.locationAlias ?? api.subject_id ?? "",
      leaseId: api.leaseId ?? null,
    },
    title:          api.title ?? "",
    description:    api.description
      ?? [api.recommendedMove, api.ifDelayed].filter(Boolean).join(" · ")
      ?? "",
    owner,
    due:            api.due ?? computeDueFromDate(dueDateStr),
    urgency,
    triage: {
      pinned:       api.triage?.pinned       ?? false,
      snoozedUntil: api.snoozedUntil ?? api.triage?.snoozedUntil ?? null,
      muted:        api.triage?.muted        ?? false,
    },
    inFocus,
    whyFactors:      api.whyFactors      ?? [],
    contextModifier: api.contextModifier ?? null,
    pathSteps:       api.pathSteps       ?? [],
    navUrl:           api.navUrl          ?? null,
    // Subject entity for lifecycle API calls (log_decision entityType/entityId).
    // focus_items schema uses subject_type + subject_id; legacy feed used leaseId.
    subjectType:      api.subject_type    ?? (api.type === "expiry" ? "lease" : null),
    subjectId:        api.subject_id      ?? api.leaseId ?? null,
    // P-50e: emitter + reversible for decision sub-type routing.
    emitter:          api.emitter   ?? null,
    reversible:       api.reversible ?? true,
    // P-50e: decisionSubType — explicit seed override wins, then derive from emitter/type/reversible.
    decisionSubType: (() => {
      if (api.decisionSubType) return api.decisionSubType;
      const emitter = api.emitter ?? null;
      const type    = api.type    ?? null;
      const rev     = api.reversible ?? true;
      if (emitter === "plan_coverage_gaps")                              return "plan-coverage-gap";
      if (emitter === "lease_decision_windows" && type === "action")     return "break-option";
      if (emitter === "lease_decision_windows" && rev === false)         return "serve-notice";
      if (emitter === "lease_decision_windows" && rev === true)          return "lease-window";
      return null;
    })(),
    // Suggested actions from the focus_items engine (e.g. workflow launch URLs).
    suggestedActions: api.suggested_actions ?? [],
    // G-34-S3: playbook provenance — non-null when action was emitted by a check
    playbookRunId:    api.playbook_run_id ?? api.playbookRunId ?? null,
    playbookContext:  api.playbook_context ?? api.playbookContext ?? null,
    // True for supplementary rows from the actions table (not focus_items).
    // Lifecycle API calls (resolve/snooze/dismiss) are invalid for these rows;
    // all such controls must be suppressed in the UI.
    isPlaybookAction: api.is_playbook_action ?? false,
    // P-61: Subject card — office/lease entity data for the drawer thumbnail.
    // Populated from seed for now; server will enrich focus_items rows in a
    // future slice. Null for Project-sourced items.
    subject:          api.subject ?? null,
    // P-61: Last reviewed — display string (e.g. "Feb 2026 · 6 mo ago") or null.
    lastReviewed:     api.last_reviewed ?? api.lastReviewed ?? null,
  };
}

/* ─── Band config (mirrors src/features/radar/RadarPage.jsx) ─────────────── */

const BANDS = [
  { key: "act_now",    label: "Act now",    desc: "overdue or due within 30 days",   pillBg: T.ragAmberBg,         pillColor: "var(--v2-amber)" },
  { key: "this_cycle", label: "This cycle", desc: "due within 90 days",              pillBg: "var(--v2-blue-tint)", pillColor: "var(--v2-accent)" },
  { key: "keep_watch", label: "Keep watch", desc: "monitoring, no immediate action", pillBg: "var(--v2-tint)",      pillColor: "var(--v2-mid)"   },
];

/* ─── Constants ──────────────────────────────────────────────────────────── */

const REFRESH_COOLDOWN_MS = 3 * 60 * 1000;

const TYPE_OPTS = ["All", "Decision", "Action", "Issue", "Watch", "Dismissed"];

// Type chip palette — text-only tinted chips used on list rows.
const TYPE_CHIP = {
  Decision: { bg: "var(--v2-blue-tint)",  color: "var(--v2-accent)"       },
  Issue:    { bg: "var(--v2-red-tint)",   color: "var(--v2-tile-red-fg)"  },
  Action:   { bg: "var(--v2-teal-tint,var(--v2-green-tint))", color: "var(--v2-teal)" },
  Watch:    { bg: "var(--v2-hair)",       color: "var(--v2-mid)"          },
};

// Type badge palette — dot + label, used in the drawer top row.
// Keys are rawType values from the DB. dot is the 7 px circle colour.
const TYPE_BADGE = {
  decision: { dot: "var(--v2-accent)",         bg: "var(--v2-blue-tint)",  color: "var(--v2-accent)",      label: "Decision"      },
  action:   { dot: "var(--v2-teal)",           bg: "var(--v2-teal-tint,var(--v2-green-tint))", color: "var(--v2-teal)", label: "Action" },
  issue:    { dot: "var(--v2-red)",            bg: "var(--v2-red-tint)",   color: "var(--v2-tile-red-fg)", label: "Issue"         },
  expiry:   { dot: "var(--v2-red)",            bg: "var(--v2-red-tint)",   color: "var(--v2-tile-red-fg)", label: "Issue"         },
  signal:   { dot: "var(--v2-mid)",            bg: "var(--v2-hair)",       color: "var(--v2-mid)",         label: "Watch"         },
  watch:    { dot: "var(--v2-mid)",            bg: "var(--v2-hair)",       color: "var(--v2-mid)",         label: "Watch"         },
  project:  { dot: "var(--v2-mid)",            bg: "var(--v2-hair)",       color: "var(--v2-mid)",         label: "Watch"         },
  review:   { dot: "var(--v2-mid)",            bg: "var(--v2-hair)",       color: "var(--v2-mid)",         label: "Watch · Review" },
  flag:     { dot: "var(--v2-amber)",          bg: "var(--v2-amber-tint,var(--v2-hair))", color: "var(--v2-amber)", label: "Flag" },
};

// Urgency score → color. Inverted from health: red = most pressing.
function urgencyColor(score) {
  if (score === null || score === undefined) return "var(--v2-mid)";
  if (score >= 80) return "var(--v2-red)";
  if (score >= 50) return "var(--v2-amber)";
  return "var(--v2-green)";
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function initials(name) {
  if (!name) return "";
  return name.trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

/* ─── TypeChip ───────────────────────────────────────────────────────────── */

// Text-only chip used on list rows.
function TypeChip({ type }) {
  const style = TYPE_CHIP[type] ?? TYPE_CHIP.Watch;
  return (
    <span
      data-testid={`type-chip-${type}`}
      style={{
        display: "inline-flex", alignItems: "center",
        fontSize: T.fsXs, fontWeight: T.fwSemibold, letterSpacing: ".03em",
        padding: "2px 10px", borderRadius: 6, whiteSpace: "nowrap",
        background: style.bg, color: style.color,
      }}
    >
      {type}
    </span>
  );
}

/* ─── TypeBadge ──────────────────────────────────────────────────────────── */

// Dot + label badge used in the drawer top row. Keyed by rawType.
function TypeBadge({ rawType }) {
  const palette = TYPE_BADGE[rawType] ?? TYPE_BADGE.signal;
  return (
    <span
      data-testid={`type-badge-${rawType ?? "unknown"}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        fontSize: T.fsXxs5, fontWeight: T.fwBold, textTransform: "uppercase",
        letterSpacing: ".04em",
        padding: "4px 9px", borderRadius: 7, whiteSpace: "nowrap",
        background: palette.bg, color: palette.color,
      }}
    >
      <span style={{
        width: 7, height: 7, borderRadius: "50%",
        background: palette.dot, flexShrink: 0,
      }} />
      {palette.label}
    </span>
  );
}

/* ─── OwnerCell ──────────────────────────────────────────────────────────── */

function OwnerCell({ owner }) {
  if (!owner) {
    return (
      <span style={{ fontSize: T.fsSm, color: "var(--v2-light)", fontStyle: "italic" }}>
        Unassigned
      </span>
    );
  }
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: T.fsSm, color: "var(--v2-dark)", minWidth: 0 }}>
      <span style={{
        width: 22, height: 22, borderRadius: "50%",
        background: "var(--v2-hair)", color: "var(--v2-mid)",
        display: "grid", placeItems: "center",
        fontSize: T.fsXs, fontWeight: T.fwBold, flexShrink: 0,
      }}>
        {initials(owner.name)}
      </span>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {owner.name}
      </span>
    </span>
  );
}

/* ─── DueCell ────────────────────────────────────────────────────────────── */

function DueCell({ due }) {
  if (!due) {
    return <span style={{ fontSize: T.fsSm, color: "var(--v2-light)" }}>—</span>;
  }
  const isOverdue = due.label === "Overdue" || (due.countdown && due.countdown.startsWith("-"));
  const color = isOverdue ? "var(--v2-red)" : "var(--v2-dark)";
  return (
    <div>
      <div style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color }}>{due.label}</div>
      <div style={{ fontSize: T.fsXs, color: "var(--v2-light)" }}>{due.countdown}</div>
    </div>
  );
}

/* ─── LedgerRow ──────────────────────────────────────────────────────────── */

// CSS grid: Type(128) · Item(1fr) · Owner(128) · Due(100) · Urgency(52) · Actions(88)
const GRID_COLS = "128px 1fr 128px 100px 52px 88px";

function LedgerRow({ item, isSelected, onSelect, triage, onTriage, onDelete, isEditor, restoreAction, compact = false }) {
  const [hover, setHover] = useState(false);
  const isPinned = triage?.pinned ?? item.triage.pinned;


  return (
    <div
      data-testid={`radar-row-${item.id}`}
      data-list-row=""
      onClick={() => onSelect(item)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        minWidth: 800,
        alignItems: "center",
        gap: 12,
        padding: compact ? "4px 18px" : "9px 18px",
        borderBottom: "1px solid var(--v2-hair)",
        cursor: "pointer",
        background: isSelected
          ? "var(--v2-blue-tint)"
          : hover ? "var(--v2-tint)" : "transparent",
        transition: "background .12s ease",
      }}
    >
      {/* Type */}
      <div><TypeChip type={item.type} /></div>

      {/* Item: title + In focus tag + Playbook tag + source */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
          {isPinned && <Pin size={13} style={{ color: "var(--v2-accent)", flexShrink: 0 }} />}
          <span style={{
            fontWeight: T.fwBold, fontSize: T.fsBase,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {item.title}
          </span>
          {item.inFocus && (
            <span style={{
              display: "inline-flex", alignItems: "center",
              fontSize: T.fsXs, fontWeight: T.fwSemibold, letterSpacing: ".03em",
              flexShrink: 0,
              color: "var(--v2-accent)", background: "var(--v2-blue-tint)",
              borderRadius: 6, padding: "2px 10px", whiteSpace: "nowrap",
            }}>
              In focus
            </span>
          )}
          {item.playbookRunId && (
            <a
              href={`/playbooks/runs/${item.playbookRunId}`}
              data-testid="playbook-source-chip"
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "inline-flex", alignItems: "center",
                fontSize: T.fsXs, fontWeight: T.fwSemibold, letterSpacing: ".03em",
                flexShrink: 0,
                color: "var(--v2-accent)", background: "var(--v2-blue-tint)",
                borderRadius: 6, padding: "2px 10px", whiteSpace: "nowrap",
                textDecoration: "none",
              }}
            >
              Playbook →
            </a>
          )}
        </div>
        <div style={{
          fontSize: T.fsXxs5, color: "var(--v2-light)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          marginTop: 1,
        }}>
          {item.source.object} · {item.source.id}
        </div>
      </div>

      {/* Owner */}
      <OwnerCell owner={item.owner} />

      {/* Due */}
      <DueCell due={item.due} />

      {/* Urgency */}
      <div style={{
        fontSize: T.fsLg, fontWeight: T.fwDisplay, textAlign: "right",
        color: urgencyColor(item.urgency),
      }}>
        {item.urgency ?? "—"}
      </div>

      {/* Actions column — Restore button (Dismissed view) or hover triage icons (normal view) */}
      {restoreAction ? (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            data-testid={`restore-${restoreAction === "unmute" ? "muted" : "snoozed"}-${item.id}`}
            title="Restore"
            onClick={(e) => { e.stopPropagation(); onTriage(item.id, restoreAction); }}
            style={{
              border: "1px solid var(--v2-line)", background: "var(--v2-card)",
              color: "var(--v2-dark)", borderRadius: 6, padding: "3px 10px",
              fontSize: T.fsXs, fontWeight: T.fwSemibold, cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Restore
          </button>
        </div>
      ) : (
        /* Actions column — empty; all triage actions live in the drawer */
        <div />
      )}
    </div>
  );
}

/* ─── SectionLabel ───────────────────────────────────────────────────────── */

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: T.fsXxs, fontWeight: T.fwBold, textTransform: "uppercase",
      letterSpacing: ".06em", color: "var(--v2-light)",
      padding: "9px 18px 5px",
    }}>
      {children}
    </div>
  );
}

/* ─── ActionBtn ──────────────────────────────────────────────────────────── */

function ActionBtn({ label, onClick, primary = false, disabled = false, testId }) {
  const bg     = primary ? "var(--v2-accent)"      : "var(--v2-card)";
  const border = primary ? "var(--v2-accent)"      : "var(--v2-line)";
  const color  = primary ? "var(--v2-card)"        : "var(--v2-dark)";
  return (
    <button
      data-testid={testId}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(e); }}
      style={{
        fontSize: T.fsSm, fontWeight: T.fwBold, padding: "7px 13px",
        borderRadius: 8, border: `1px solid ${border}`,
        background: bg, color,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );
}

/* ─── DrawerPrimaryActions ───────────────────────────────────────────────── */

// Decision outcome buttons (Renew / Negotiate / Exit / Defer)
const DECISION_OUTCOMES = [
  { label: "Renew",     outcome: "Renew"     },
  { label: "Negotiate", outcome: "Negotiate" },
  { label: "Exit",      outcome: "Exit"      },
  { label: "Defer",     outcome: "Defer"     },
];

function DrawerPrimaryActions({ item, onResolve, resolving }) {
  const navigate = useNavigate();
  const [showNotes,   setShowNotes]   = useState(false);
  const [notesText,   setNotesText]   = useState("");
  const [showAssign,  setShowAssign]  = useState(false);
  const [assignText,  setAssignText]  = useState(item.owner?.name ?? "");

  const isDisabled = !!resolving;

  // Suppressed for playbook-action rows (lifecycle API not valid for actions table)
  if (item.isPlaybookAction) return null;

  const sectionStyle = {
    borderTop: "1px solid var(--v2-hair)",
    marginTop: 12, paddingTop: 12, marginBottom: 4,
  };
  const labelStyle = {
    fontSize: T.fsXxs, fontWeight: T.fwBold, textTransform: "uppercase",
    letterSpacing: ".05em", color: "var(--v2-light)", marginBottom: 8,
  };

  switch (item.rawType) {
    case "decision": {
      // P-50e: serve-notice items use "Serve / Waive" instead of the standard four-verb set.
      const isServeNotice = item.decisionSubType === "serve-notice";
      const decisionOutcomes = isServeNotice
        ? [{ label: "Serve", outcome: "Serve" }, { label: "Waive", outcome: "Waive" }]
        : DECISION_OUTCOMES;
      return (
        <div style={sectionStyle}>
          <div style={labelStyle}>Log decision</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: showNotes ? 10 : 0 }}>
            {decisionOutcomes.map(({ label, outcome }, idx) => (
              <ActionBtn
                key={outcome}
                label={label}
                primary={idx === 0}
                disabled={isDisabled}
                testId={`drawer-log-decision-${outcome.toLowerCase()}`}
                onClick={() => {
                  const notes = notesText.trim();
                  onResolve(item.id, "log_decision", {
                    decision:   notes ? `${outcome}: ${notes}` : outcome,
                    ownerName:  null,
                    targetDate: null,
                    // Use the subject entity from the focus_items row so the
                    // portfolio_decisions record is linked to the real lease/project,
                    // not the focus_item UUID.  Falls back to "lease" + focus_item id
                    // only when the feed doesn't supply subject fields.
                    entityType: item.subjectType ?? "lease",
                    entityId:   item.subjectId   ?? item._dbId,
                  });
                }}
              />
            ))}
            <ActionBtn
              label="Add notes…"
              disabled={isDisabled}
              testId="drawer-log-decision-notes"
              onClick={() => setShowNotes((v) => !v)}
            />
          </div>
          {showNotes && (
            <textarea
              data-testid="drawer-log-decision-textarea"
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              placeholder="Optional notes…"
              rows={2}
              style={{
                width: "100%", resize: "vertical", fontSize: T.fsSm5,
                padding: "8px 10px", borderRadius: 8,
                border: "1px solid var(--v2-line)", background: "var(--v2-card)",
                boxSizing: "border-box", marginTop: 6,
              }}
            />
          )}
        </div>
      );
    }

    case "signal":
    case "watch":
      return (
        <div style={sectionStyle}>
          <ActionBtn
            label="Acknowledge"
            primary
            disabled={isDisabled}
            testId="drawer-acknowledge"
            onClick={() => onResolve(item.id, "acknowledge")}
          />
        </div>
      );

    case "review":
      return (
        <div style={sectionStyle}>
          <ActionBtn
            label="Mark reviewed"
            primary
            disabled={isDisabled}
            testId="drawer-mark-reviewed"
            onClick={() => onResolve(item.id, "mark_reviewed")}
          />
        </div>
      );

    case "flag":
      return (
        <div style={sectionStyle}>
          <ActionBtn
            label="Fix data"
            primary
            disabled={isDisabled}
            testId="drawer-fix-data"
            onClick={() => onResolve(item.id, "mark_fixed")}
          />
        </div>
      );

    case "issue":
    case "expiry":
      return (
        <div style={sectionStyle}>
          {showAssign ? (
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
              <input
                data-testid="drawer-assign-input"
                type="text"
                placeholder="Assignee email"
                value={assignText}
                onChange={(e) => setAssignText(e.target.value)}
                style={{ fontSize: T.fsSm, padding: "6px 10px", borderRadius: 8, border: "1px solid var(--v2-line)", flex: 1 }}
              />
              <ActionBtn
                label="Save"
                primary
                disabled={!assignText.trim()}
                testId="drawer-assign-save"
                onClick={() => {
                  setShowAssign(false);
                  onResolve(item.id, "mark_resolved", { resolution: `Assigned to ${assignText.trim()}` });
                }}
              />
              <ActionBtn label="Cancel" testId="drawer-assign-cancel" onClick={() => setShowAssign(false)} />
            </div>
          ) : (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <ActionBtn
                label="Assign"
                disabled={isDisabled}
                testId="drawer-assign"
                onClick={() => setShowAssign(true)}
              />
              <ActionBtn
                label="Mark resolved"
                primary
                disabled={isDisabled}
                testId="drawer-mark-resolved"
                onClick={() => onResolve(item.id, "mark_resolved")}
              />
            </div>
          )}
        </div>
      );

    case "action": {
      // P-50e: plan-coverage-gap → single "Set plan" button navigating to Market Plan.
      // Use subjectId (the market's UUID from focus_items.subject_id) — NOT source.id,
      // which resolves to a location_id and would produce a broken expandMarket param.
      if (item.decisionSubType === "plan-coverage-gap") {
        const marketId = item.subjectId ?? null;
        return (
          <div style={sectionStyle}>
            <ActionBtn
              label="Set plan"
              primary
              disabled={isDisabled}
              testId="drawer-action-set-plan"
              onClick={() => navigate(buildMarketPlanUrl("plan", marketId))}
            />
          </div>
        );
      }

      // P-50e: break-option → "Decide: Exercise / Waive".
      if (item.decisionSubType === "break-option") {
        return (
          <div style={sectionStyle}>
            <div style={labelStyle}>Decide</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[{ label: "Exercise", outcome: "Exercise" }, { label: "Waive", outcome: "Waive" }].map(({ label, outcome }, idx) => (
                <ActionBtn
                  key={outcome}
                  label={label}
                  primary={idx === 0}
                  disabled={isDisabled}
                  testId={`drawer-action-${outcome.toLowerCase()}`}
                  onClick={() => onResolve(item.id, "log_decision", {
                    decision:   outcome,
                    entityType: item.subjectType ?? "lease",
                    entityId:   item.subjectId   ?? item._dbId,
                  })}
                />
              ))}
            </div>
          </div>
        );
      }

      // Default: workflow launch or mark done.
      // Resolve launch URL: prefer suggested_actions workflow entry (live feed)
      // then fall back to the legacy navUrl field.
      const workflowAction = (item.suggestedActions ?? []).find((a) => a.kind === "workflow");
      const launchUrl = workflowAction?.url ?? item.navUrl ?? null;
      return (
        <div style={sectionStyle}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <ActionBtn
              label="▶ Start"
              disabled={isDisabled}
              testId="drawer-action-start"
              onClick={() => {
                if (launchUrl) {
                  window.location.href = launchUrl;
                } else {
                  onResolve(item.id, "mark_done", { resolution: "Workflow started" });
                }
              }}
            />
            <ActionBtn
              label="Mark done"
              primary
              disabled={isDisabled}
              testId="drawer-action-done"
              onClick={() => onResolve(item.id, "mark_done")}
            />
          </div>
        </div>
      );
    }

    default:
      return (
        <div style={sectionStyle}>
          <ActionBtn
            label="Resolve"
            primary
            disabled={isDisabled}
            testId="drawer-resolve"
            onClick={() => onResolve(item.id, null)}
          />
        </div>
      );
  }
}

/* ─── buildWhyText / buildNextStep ──────────────────────────────────────── */

// Pure template functions — no LLM, no network calls.
// Compose 2–3 sentences using the item's own fields.

// Converts a due object into a grammatical time phrase for use in sentences.
// Returns:
//   "passed N days ago"  — when label is "Overdue" (parses days from countdown)
//   "today"              — when label is "Today"
//   "by <label>"         — otherwise (e.g. "by Apr 30, 2025")
function buildDuePart(due) {
  if (!due) return "soon";
  if (due.label === "Today") return "today";
  if (due.label === "Overdue") {
    // countdown is e.g. "-12d" for 12 days overdue.
    const match = due.countdown ? String(due.countdown).match(/-(\d+)d/) : null;
    if (match) {
      const n = parseInt(match[1], 10);
      return `passed ${n} day${n === 1 ? "" : "s"} ago`;
    }
    return "passed its deadline";
  }
  return `by ${due.label}`;
}

function buildWhyText(item) {
  const srcLabel = item.subject?.name || item.source?.label || "this location";
  const duePart  = buildDuePart(item.due);

  switch (item.rawType) {
    case "review":
      return `${srcLabel} is due for its portfolio review. Regular reviews keep this office's headcount, lease, and utilization current so its decisions stay sound. It hasn't been reviewed this cycle — nothing looks wrong, this is a scheduled check-in, not a problem.`;

    case "decision":
      if (item.subjectType === "notice") {
        return `The notice deadline for ${srcLabel} ${duePart}. You can still serve or waive the notice, but the window is closing and inaction is a decision in itself.`;
      }
      return `${srcLabel} needs a logged decision — the notice deadline ${duePart}. A logged decision unlocks the next step in the portfolio plan and keeps the landlord timeline on track.`;

    case "flag":
      return `A data quality flag was raised on ${srcLabel}. Flags surface because accurate data is a prerequisite for reliable planning — this one may be affecting downstream scores or reports.`;

    case "signal":
    case "watch":
    case "project":
      return `A portfolio signal was detected at ${srcLabel}. Signals are early indicators that may require monitoring or action before they become issues. No immediate action is required — this is here so it stays on your radar.`;

    case "issue":
    case "expiry":
      return `An issue was identified at ${srcLabel}. Issues surface when something needs attention that could affect plans or decisions. Leaving it unresolved may increase the portfolio's risk exposure.`;

    case "action":
      if (item.decisionSubType === "break-option") {
        return `${srcLabel} has a break option to decide ${duePart}. Exercising or waiving needs a logged decision before the window closes — inaction defaults to a waiver in most leases.`;
      }
      return `An action is pending for ${srcLabel}. Completing this action keeps the portfolio's execution on track and may unblock downstream milestones.`;

    default:
      return `This item was surfaced by the portfolio engine because it may need attention. Review the details and take the appropriate action when ready.`;
  }
}

function buildNextStep(item) {
  switch (item.rawType) {
    case "review":
      return "Open the location, confirm the numbers look right, then mark it reviewed to reset the cadence.";

    case "decision":
      if (item.subjectType === "notice") {
        return "Review the notice terms, then log your decision: serve the notice or waive it.";
      }
      return "Model the options in What-If, then log a decision before the deadline.";

    case "flag":
      return "Open the location or lease record, correct the data, and mark it fixed.";

    case "signal":
    case "watch":
    case "project":
      return "Monitor this item. If conditions change, return here to escalate or take action.";

    case "issue":
    case "expiry":
      return "Assign an owner if unowned, investigate the root cause, then mark it resolved.";

    case "action":
      return "Open the relevant record, complete the action, then mark it done.";

    default:
      return "Review this item and take the appropriate action.";
  }
}

/* ─── SubjectCard ────────────────────────────────────────────────────────── */

// Clickable office / lease subject card shown below the drawer title.
//
// Data priority:
//   1. item.subject  — server-enriched (seats, assigned, leaseSummary); populated
//      from the demo seed now, server enrichment tracked as a separate task.
//   2. source fields — always available from the live focus-items feed; used as
//      a fallback so the card renders something useful for every Office/Lease item
//      even before server enrichment is wired up.
//   3. null          — Project/Signal source objects; renders nothing.
function SubjectCard({ item, navigate }) {
  const isLocationSource = item.source.object === "Office" || item.source.object === "Lease";

  // Build a display subject: prefer server-enriched, fall back to source fields.
  const subject = item.subject ?? (isLocationSource ? {
    name:         item.source.label || item.source.id,
    id:           item.source.id,
    stage:        null,
    region:       null,
    seats:        null,
    assigned:     null,
    leaseSummary: null,
  } : null);

  if (!subject) return null;

  const statParts = [
    subject.seats != null     ? `${subject.seats} seats`        : null,
    subject.assigned != null  ? `${subject.assigned} assigned`  : null,
    subject.leaseSummary      ? subject.leaseSummary            : null,
  ].filter(Boolean);

  return (
    <div
      data-testid="drawer-subject-card"
      onClick={() => navigate(buildLocationUrl(item.source.id))}
      style={{
        margin: "0 0 14px", border: "1px solid var(--v2-line)",
        borderRadius: 11, overflow: "hidden",
        display: "flex", alignItems: "stretch",
        cursor: "pointer",
      }}
    >
      {/* Thumbnail — real photo when available, gradient placeholder otherwise */}
      {subject.imageUrl ? (
        <img
          src={subject.imageUrl}
          alt={subject.name}
          style={{
            flex: "0 0 66px",
            width: 66,
            objectFit: "cover",
            display: "block",
            alignSelf: "stretch",
          }}
        />
      ) : (
        <div style={{
          flex: "0 0 66px",
          background: "linear-gradient(135deg, var(--v2-line), var(--v2-mid))",
        }} />
      )}
      {/* Card body */}
      <div style={{ padding: "9px 11px", minWidth: 0 }}>
        <div style={{ fontWeight: T.fwBold, fontSize: T.fsBase, lineHeight: 1.3 }}>
          {subject.name}
        </div>
        <div style={{ fontSize: T.fsXs5, color: "var(--v2-mid)", marginTop: 2 }}>
          {[subject.id, subject.stage, subject.region].filter(Boolean).join(" · ")}
        </div>
        {statParts.length > 0 && (
          <div style={{ fontSize: T.fsXs5, color: "var(--v2-mid)", marginTop: 5 }}>
            {statParts.join(" · ")}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── DrawerPanel ────────────────────────────────────────────────────────── */

/* ─── DeleteConfirmDialog ────────────────────────────────────────────────── */

function DeleteConfirmDialog({ item, onConfirm, onCancel }) {
  if (!item) return null;
  return (
    <div
      data-testid="delete-confirm-overlay"
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0, zIndex: 999,
        background: "rgba(16,36,58,.35)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        data-testid="delete-confirm-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--v2-card)", borderRadius: 12,
          boxShadow: "0 8px 32px rgba(16,36,58,.18)",
          padding: "24px 26px 20px", maxWidth: 380, width: "90%",
        }}
      >
        <h3 style={{ margin: "0 0 8px", fontSize: T.fsLg, fontWeight: T.fwBold }}>
          Remove this item?
        </h3>
        <p style={{ margin: "0 0 6px", fontSize: T.fsSm5, color: "var(--v2-dark)", fontWeight: T.fwSemibold }}>
          {item.title}
        </p>
        <p style={{ margin: "0 0 20px", fontSize: T.fsSm, color: "var(--v2-mid)", lineHeight: 1.5 }}>
          This moves the item to Recently deleted. You can restore it for 30 days from Admin → Recently deleted.
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            data-testid="delete-confirm-cancel"
            onClick={onCancel}
            style={{
              border: "1px solid var(--v2-line)", background: "var(--v2-card)",
              color: "var(--v2-dark)", borderRadius: 8, padding: "8px 16px",
              fontSize: T.fsSm, fontWeight: T.fwSemibold, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            data-testid="delete-confirm-ok"
            onClick={onConfirm}
            style={{
              border: "1px solid var(--v2-red)", background: "var(--v2-red)",
              color: "var(--v2-card)", borderRadius: 8, padding: "8px 16px",
              fontSize: T.fsSm, fontWeight: T.fwBold, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <Trash2 size={13} />
            Remove item
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── DrawerPanel ────────────────────────────────────────────────────────── */

function DrawerPanel({ item, triage, onTriage, onDelete, isEditor, onClose, onResolve, resolving }) {
  const featureFlags    = useFeatureFlags();
  const scoringArchived = !!featureFlags[FEATURE_SCORING_MODEL_ARCHIVED];
  const navigate        = useNavigate();
  const [overflowOpen, setOverflowOpen] = useState(false);

  if (!item) return null;

  const isPinned       = triage?.pinned ?? item.triage.pinned;
  const isLeaseSource  = item.source.object === "Lease";
  const isOfficeSource = item.source.object === "Office";
  const showLocation   = isLeaseSource || isOfficeSource;

  // Overflow items are rendered in the DOM always (display controlled by state)
  // so existing data-testid assertions continue to work without opening the
  // popover first. Only the visual visibility is toggled.
  const hasOverflow = (
    !item.isPlaybookAction ||     // pin/remove live here for non-playbook items
    isLeaseSource            ||   // open-lease
    !!item.playbookRunId          // open-playbook-run
  );

  const dueIsAmber = item.due && item.due.label !== "Overdue" && !item.due.label.startsWith("-");
  const dueColor   = item.due?.label === "Overdue"
    ? "var(--v2-red)"
    : dueIsAmber
      ? "var(--v2-amber)"
      : "var(--v2-dark)";

  const whyText  = buildWhyText(item);
  const nextText = buildNextStep(item);

  return (
    <div style={{ padding: "14px 16px 18px" }}>

      {/* ── Top row: TypeBadge · In Focus chip · close ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <TypeBadge rawType={item.rawType} />
        {item.inFocus && (
          <span style={{
            fontSize: T.fsXs, fontWeight: T.fwBold,
            color: "var(--v2-accent)", background: "var(--v2-blue-tint)",
            borderRadius: 6, padding: "3px 8px",
          }}>
            In focus
          </span>
        )}
        <button
          data-testid="drawer-close"
          onClick={onClose}
          style={{
            marginLeft: "auto", width: 26, height: 26, borderRadius: "50%",
            background: "var(--v2-hair)", color: "var(--v2-mid)",
            border: "none", display: "grid", placeItems: "center",
            fontSize: T.fsBase, cursor: "pointer",
          }}
        >
          ✕
        </button>
      </div>

      {/* ── Title ── */}
      <h2 style={{ fontSize: T.fsLg5, fontWeight: T.fwBold, margin: "0 0 2px", lineHeight: 1.3 }}>
        {item.title}
      </h2>

      {/* ── Source hint ── */}
      <div style={{ fontSize: T.fsXs5, color: "var(--v2-light)", marginBottom: 12 }}>
        {item.source.label}
      </div>

      {/* ── Subject card ── */}
      <SubjectCard item={item} navigate={navigate} />

      {item.playbookContext && (
        <div
          data-testid="drawer-playbook-context"
          style={{
            margin: "0 0 14px", padding: "10px 12px", borderRadius: 9,
            background: "var(--v2-blue-tint)", border: "1px solid var(--v2-line)",
            fontSize: T.fsSm5, color: "var(--v2-dark)",
          }}
        >
          <strong>Decision handoff · {item.playbookContext.templateVariant ?? "FULL"}</strong>
          {item.playbookContext.decision?.text && (
            <div style={{ marginTop: 4 }}>{item.playbookContext.decision.text}</div>
          )}
          {item.playbookContext.subject?.locationAlias && (
            <div style={{ marginTop: 3, color: "var(--v2-mid)" }}>
              Subject office: {item.playbookContext.subject.locationAlias}
            </div>
          )}
        </div>
      )}

      {/* ── Meta rows: Due · Owner · Last reviewed ── */}
      <div style={{ marginBottom: 12 }}>
        {/* Due */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          fontSize: T.fsSm5, padding: "7px 0",
          borderTop: "1px solid var(--v2-hair)",
        }}>
          <span style={{ color: "var(--v2-mid)" }}>Due</span>
          {item.due ? (
            <span style={{ fontWeight: T.fwBold, color: dueColor }}>
              {item.due.label}
              {item.due.countdown ? ` · ${item.due.countdown}` : ""}
            </span>
          ) : (
            <span style={{ color: "var(--v2-light)" }}>—</span>
          )}
        </div>
        {/* Owner */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          fontSize: T.fsSm5, padding: "7px 0",
          borderTop: "1px solid var(--v2-hair)",
        }}>
          <span style={{ color: "var(--v2-mid)" }}>Owner</span>
          <OwnerCell owner={item.owner} />
        </div>
        {/* Last reviewed */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          fontSize: T.fsSm5, padding: "7px 0",
          borderTop: "1px solid var(--v2-hair)",
        }}>
          <span style={{ color: "var(--v2-mid)" }}>Last reviewed</span>
          <span style={{ fontWeight: T.fwBold }}>
            {item.lastReviewed ?? "—"}
          </span>
        </div>
      </div>

      {/* ── Why this is here ── */}
      <div style={{
        background: "var(--v2-tint)", border: "1px solid var(--v2-line)",
        borderRadius: 11, padding: "11px 13px", marginBottom: 10,
      }}>
        <div style={{
          fontSize: T.fsXxs, fontWeight: T.fwBold, textTransform: "uppercase",
          letterSpacing: ".05em", color: "var(--v2-light)", marginBottom: 5,
        }}>
          Why this is here
        </div>
        <p style={{ margin: 0, fontSize: T.fsSm5, color: "var(--v2-dark)", lineHeight: 1.55 }}>
          {whyText}
        </p>
      </div>

      {/* ── Suggested next step ── */}
      <div style={{
        background: "var(--v2-blue-tint)", border: "1px solid var(--v2-blue-tint)",
        borderRadius: 11, padding: "10px 13px", marginBottom: 14,
        display: "flex", gap: 9, alignItems: "flex-start",
      }}>
        <span style={{ color: "var(--v2-accent)", fontWeight: T.fwBold, fontSize: T.fsBase, lineHeight: 1.3, flexShrink: 0 }}>
          →
        </span>
        <div style={{ fontSize: T.fsSm5, color: "var(--v2-accent)", lineHeight: 1.5 }}>
          <b>Next:</b> {nextText}
        </div>
      </div>

      {/* ── Primary actions — editor only, suppressed for playbook-action rows ── */}
      {isEditor && !item.isPlaybookAction && (
        <DrawerPrimaryActions item={item} onResolve={onResolve} resolving={resolving} />
      )}

      {/* ── Secondary action row ── */}
      <div style={{ display: "flex", gap: 8, marginTop: 10, position: "relative" }}>

        {/* Open location — Office and Lease items; guard item.source.id (P-60) */}
        {showLocation && item.source.id && (
          <button
            data-testid="drawer-open-location"
            onClick={() => navigate(buildLocationUrl(item.source.id))}
            style={{
              flex: 1,
              border: "1px solid var(--v2-line)", background: "var(--v2-card)",
              color: "var(--v2-dark)", borderRadius: 9, padding: "9px 10px",
              fontSize: T.fsSm, fontWeight: T.fwBold, cursor: "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            <ExternalLink size={13} />
            Open location
          </button>
        )}

        {/* Snooze — editor + !isPlaybookAction */}
        {isEditor && !item.isPlaybookAction && (
          <button
            data-testid="drawer-snooze"
            onClick={() => onTriage(item.id, "snooze")}
            style={{
              flex: 1,
              border: "1px solid var(--v2-line)", background: "var(--v2-card)",
              color: "var(--v2-dark)", borderRadius: 9, padding: "9px 10px",
              fontSize: T.fsSm, fontWeight: T.fwBold, cursor: "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            <Clock size={13} />
            Snooze
          </button>
        )}

        {/* ··· overflow toggle — always shown when there are overflow items */}
        {hasOverflow && (
          <button
            data-testid="drawer-more"
            onClick={() => setOverflowOpen((v) => !v)}
            style={{
              flex: "0 0 42px",
              border: "1px solid var(--v2-line)", background: "var(--v2-card)",
              color: "var(--v2-mid)", borderRadius: 9,
              display: "grid", placeItems: "center",
              cursor: "pointer", fontWeight: T.fwBold, fontSize: T.fsBase,
            }}
          >
            <MoreHorizontal size={16} />
          </button>
        )}

        {/* ··· overflow popover — rendered in DOM always; display toggled for testability */}
        <div
          data-testid="drawer-overflow-popover"
          style={{
            display: overflowOpen ? "block" : "none",
            position: "absolute", bottom: "calc(100% + 6px)", right: 0,
            background: "var(--v2-card)", border: "1px solid var(--v2-line)",
            borderRadius: 10, boxShadow: "0 4px 16px rgba(16,36,58,.13)",
            minWidth: 170, zIndex: 20, padding: "4px 0",
          }}
        >
          {/* Open lease — Lease items only */}
          {isLeaseSource && (
            <button
              data-testid="drawer-open-lease"
              onClick={() => {
                setOverflowOpen(false);
                navigate(buildLeaseUrl(item.source.leaseId ?? item.source.id));
              }}
              style={overflowItemStyle}
            >
              <ExternalLink size={13} style={{ flexShrink: 0 }} />
              Open lease
            </button>
          )}

          {/* Pin / Unpin — !isPlaybookAction */}
          {!item.isPlaybookAction && (
            <button
              data-testid="drawer-pin"
              onClick={() => { setOverflowOpen(false); onTriage(item.id, "pin"); }}
              style={overflowItemStyle}
            >
              <Pin size={13} style={{ flexShrink: 0 }} />
              {isPinned ? "Unpin" : "Pin"}
            </button>
          )}

          {/* Mute / Unmute — editor + !isPlaybookAction */}
          {isEditor && !item.isPlaybookAction && (
            <button
              data-testid="drawer-mute"
              onClick={() => { setOverflowOpen(false); onTriage(item.id, "mute"); }}
              style={overflowItemStyle}
            >
              <BellOff size={13} style={{ flexShrink: 0 }} />
              {triage?.muted ?? item.triage.muted ? "Unmute" : "Mute"}
            </button>
          )}

          {/* Open playbook run — when playbookRunId is set */}
          {item.playbookRunId && (
            <button
              data-testid="drawer-open-playbook"
              onClick={() => { setOverflowOpen(false); navigate(`/playbooks/runs/${item.playbookRunId}`); }}
              style={overflowItemStyle}
            >
              <ExternalLink size={13} style={{ flexShrink: 0 }} />
              Open Playbook run
            </button>
          )}

          {/* Remove — editor + !isPlaybookAction */}
          {isEditor && !item.isPlaybookAction && (
            <button
              data-testid="drawer-delete"
              onClick={() => { setOverflowOpen(false); onDelete(item.id); }}
              style={{ ...overflowItemStyle, color: "var(--v2-red)" }}
            >
              <Trash2 size={13} style={{ flexShrink: 0 }} />
              Remove item
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Shared style for overflow popover items
const overflowItemStyle = {
  display: "flex", alignItems: "center", gap: 8,
  width: "100%", padding: "8px 14px",
  background: "none", border: "none",
  fontSize: T.fsSm, fontWeight: T.fwSemibold,
  color: "var(--v2-dark)", cursor: "pointer",
  textAlign: "left",
};

/* ─── Sort utility ───────────────────────────────────────────────────────── */

// Parse a countdown string like "20d", "-3d", "69d" → integer days.
// Returns Infinity for items with no due date (sort to end).
function parseDueDays(due) {
  if (!due || !due.countdown) return Infinity;
  const m = due.countdown.match(/^(-?\d+)d$/);
  return m ? parseInt(m[1], 10) : Infinity;
}

// Default sort: due soonest → urgency descending → id (stable tiebreaker).
function sortDueSoonest(a, b) {
  const da = parseDueDays(a.due);
  const db = parseDueDays(b.due);
  if (da !== db) return da - db;
  const ua = a.urgency ?? -1;
  const ub = b.urgency ?? -1;
  if (ub !== ua) return ub - ua;
  return a.id < b.id ? -1 : 1;
}

// Alternate sort: urgency descending → due soonest → id (stable tiebreaker).
// Null urgency scores sort to the end (treated as -1 for comparison).
function sortByUrgency(a, b) {
  const ua = a.urgency ?? -1;
  const ub = b.urgency ?? -1;
  if (ub !== ua) return ub - ua;
  const da = parseDueDays(a.due);
  const db = parseDueDays(b.due);
  if (da !== db) return da - db;
  return a.id < b.id ? -1 : 1;
}

/* ─── InFocusRadarPage ───────────────────────────────────────────────────── */

export function InFocusRadarPage({ currentRole = "viewer" }) {
  const [ledgerElement, setLedgerElement] = useState(null);
  const ledgerOverflow = useListOverflow({ element: ledgerElement, scrollRef: { current: ledgerElement }, grid: true, pinLast: true });
  const navigate = useNavigate();
  const isEditor = currentRole === "editor" || currentRole === "admin";
  const featureContext = useFeatureFlagContext();
  const radarEnabled = homeNavEnabled(featureContext, "radar");
  const planEnabled = homeNavEnabled(featureContext, "plan");
  const featureFlags = useFeatureFlags();
  const decisionsRegisterEnabled = featureFlags[RADAR_DECISIONS_FLAG] !== false;

  // INFOCUS-TRAY-URL-STATE: Single source of truth for the intended focus ID.
  // Seeded from ?focus= at mount so a direct URL or browser Back re-opens the
  // correct tray immediately. Per project memory: useSearchParams() is forbidden
  // (stalls React 18 route transitions).
  // G-34-S3: also accepts ?highlight= (deep-link from PlaybookRunPage provenance
  // banner) as a fallback when ?focus= is absent; treated identically.
  const intendedFocusRef = useRef(
    new URLSearchParams(window.location.search).get("focus")
    ?? new URLSearchParams(window.location.search).get("highlight")
    ?? null
  );

  const [typeFilter,     setTypeFilter]     = useState("All");
  const [sortPrimary,    setSortPrimary]    = useState({ key: "due_soonest", dir: "asc" });
  const [sortSecondary,  setSortSecondary]  = useState({ key: null, dir: "asc" });
  const [search,         setSearch]         = useState("");
  const [statFilter,     setStatFilter]     = useState(null);
  const [filterOpen,     setFilterOpen]     = useState(false);

  // Compact-mode density persisted to localStorage via useUiPrefs.
  const [uiPrefs, setUiPrefs] = useUiPrefs(UI_PREFS_INFOCUS_RADAR_V1, { compact: false });
  const compact = uiPrefs.compact ?? false;
  const toggleCompact = useCallback(() => setUiPrefs((p) => ({ ...p, compact: !p.compact })), [setUiPrefs]);
  const filterRef                     = useRef(null);
  // INFOCUS-TRAY-URL-STATE: Do NOT seed selectedId directly from the URL.
  // The item list may not be available yet when the URL is read (e.g. when a
  // real API feed replaces the static seed). The sync-on-load effect below
  // opens the correct tray once items are confirmed to exist, and clears the
  // URL gracefully when the item is gone.
  const [selectedId, setSelectedId]   = useState(null);
  const [mutedOpen,  setMutedOpen]    = useState(false);

  // Items state — currently the static seed, but modeled as async state so the
  // sync-on-load guard works correctly regardless of whether items come from a
  // static seed or a real API call. null = not yet loaded.
  const [items, setItems]             = useState(null);
  const [itemsLoaded, setItemsLoaded] = useState(false);

  // Close filter dropdown on outside click.
  useEffect(() => {
    if (!filterOpen) return undefined;
    function onMouseDown(e) {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [filterOpen]);

  // Refresh state — mirrors the Open Roles FreshnessBar pattern.
  const [reloadKey,       setReloadKey]       = useState(0);
  const [refreshing,      setRefreshing]      = useState(false);
  const [cooldownUntil,   setCooldownUntil]   = useState(0);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(() => Date.now());

  const isCooldown = cooldownUntil > Date.now();
  const isDisabled = refreshing || isCooldown;

  // Age label for the "Updated X ago" timestamp — same logic as FreshnessBar.
  const ageLabel = useMemo(() => {
    const ms  = Date.now() - lastRefreshedAt;
    const min = Math.floor(ms / 60_000);
    if (min < 1)  return "just now";
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
  }, [lastRefreshedAt, refreshing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch items from the live API feed — keyed to reloadKey so the Refresh
  // button re-fetches without unmounting. Items are never available
  // synchronously (promise always resolves asynchronously), which is required
  // so the sync-on-load ?focus= URL guard fires after items are confirmed.
  useEffect(() => {
    if (!radarEnabled) return undefined;
    let cancelled = false;
    setItemsLoaded(false);
    getFocusItemsFeed({ sort: "priority" })
      .then((r) => {
        if (!cancelled) {
          setItems((r.items ?? []).map(adaptApiItem));
          setItemsLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setItemsLoaded(true);
        }
      });
    return () => { cancelled = true; };
  }, [radarEnabled, reloadKey]);

  // Refresh handler — mirrors Open Roles' onRefresh pattern.
  async function handleRefresh() {
    if (isDisabled) return;
    setRefreshing(true);
    setReloadKey((n) => n + 1);
    // Wait for the seed / future API to settle (microtask).
    await Promise.resolve();
    setLastRefreshedAt(Date.now());
    const until = Date.now() + REFRESH_COOLDOWN_MS;
    setCooldownUntil(until);
    setRefreshing(false);
    // Auto-expire the cooldown after the window closes.
    setTimeout(() => setCooldownUntil(0), REFRESH_COOLDOWN_MS);
  }

  // Dismissed cards — router state is the fast-path seed (available immediately
  // when the user arrives via Pulse home "View all"). The API fetch always runs
  // on mount so direct navigation, browser refresh, and back/forward navigation
  // also populate the Acknowledged/Snoozed groups correctly.
  const routerLoc     = useLocation();
  const routerSeed    = routerLoc.state?.dismissedCards ?? null;

  // apiDismissed starts from router seed so the groups render without a loading
  // flash when the user arrived from Pulse home. It is then overwritten by the
  // API response on mount (null = not yet loaded).
  const [apiDismissed, setApiDismissed] = useState(routerSeed);

  useEffect(() => {
    if (!radarEnabled) return undefined;
    let cancelled = false;
    getPulseLocationRisk()
      .then((result) => {
        if (!cancelled) {
          const dismissed = Array.isArray(result?.dismissedCards) ? result.dismissedCards : [];
          setApiDismissed(dismissed);
        }
      })
      .catch(() => {
        if (!cancelled && apiDismissed === null) {
          // If we have no seed from router state and the API fails, show empty.
          setApiDismissed([]);
        }
      });
    return () => { cancelled = true; };
  }, [radarEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Muted items — fetched from GET /muted on mount so they survive page reloads.
  // After mount, the list is managed optimistically (add on mute, remove on unmute).
  // A full page reload re-hydrates it from the server, ensuring persistence.
  //
  // Race guard: once the user mutates (mute/unmute), the pending mount fetch must
  // not overwrite the optimistic state. hasMutatedMutedRef is set true on first
  // mutation so the fetch callback becomes a no-op for the rest of the session.
  const hasMutatedMutedRef = useRef(false);
  const [apiMutedItems, setApiMutedItems] = useState([]);
  useEffect(() => {
    if (!radarEnabled) return undefined;
    let cancelled = false;
    getMutedFocusItems()
      .then((r) => {
        if (!cancelled && !hasMutatedMutedRef.current) {
          setApiMutedItems((r.items ?? []).map(adaptApiItem));
        }
      })
      .catch(() => {
        if (!cancelled && !hasMutatedMutedRef.current) setApiMutedItems([]);
      });
    return () => { cancelled = true; };
  }, [radarEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // INFOCUS-TRAY-URL-STATE: Sync URL-driven focus → selectedId once items are
  // available. This handles three cases in one effect (matching PulsePage's
  // intendedPeekRef pattern):
  //   1. Initial deep-link: ?focus= was in the URL at mount; fires once items load.
  //   2. Back-before-load: popstate updated the ref before items arrived; this
  //      runs when itemsLoaded flips true and opens the correct tray.
  //   3. Graceful stale-URL recovery: item no longer exists in the feed; clears
  //      the ?focus param instead of leaving an empty open drawer.
  // Does NOT run while loading to avoid a premature "no match → clear URL" result.
  useEffect(() => {
    if (!radarEnabled) return undefined;
    if (!itemsLoaded || !items) return;
    const peekId = intendedFocusRef.current;
    if (!peekId) return;
    // Match by adapted id (dedupe_key or UUID) or underlying DB id (_dbId).
    // The _dbId fallback handles ?highlight=<sourceId> from PlaybookRunPage
    // provenance links where the sourceId is a DB UUID rather than dedupe_key.
    const match = items.find((i) => i.id === peekId || i._dbId === peekId);
    if (match) {
      setSelectedId(match.id);
    } else {
      // Item no longer exists — clear the stale URL param gracefully.
      intendedFocusRef.current = null;
      const p = new URLSearchParams(window.location.search);
      p.delete("focus");
      p.delete("highlight");
      const qs = p.toString();
      navigate(qs ? "?" + qs : window.location.pathname, { replace: true });
    }
  }, [radarEnabled, itemsLoaded, items]); // eslint-disable-line react-hooks/exhaustive-deps

  // INFOCUS-TRAY-URL-STATE: Listen for browser Back/Forward while mounted.
  // Same-route transitions keep InFocusRadarPage mounted, so the initial ref
  // would be stale without this.
  // - If items are already loaded: resolve immediately (lookup + set/clear).
  // - If items are not yet loaded: update the ref only; the sync-on-load effect
  //   above will handle it when itemsLoaded flips true.
  useEffect(() => {
    if (!radarEnabled) return undefined;
    function onPopState() {
      const focusId = new URLSearchParams(window.location.search).get("focus") ?? null;
      intendedFocusRef.current = focusId;
      if (!focusId) {
        setSelectedId(null);
        return;
      }
      if (!itemsLoaded || !items) {
        // Items not yet available — sync-on-load effect will open the tray once
        // items arrive. Nothing else to do here.
        return;
      }
      const match = items.find((i) => i.id === focusId || i._dbId === focusId);
      if (match) {
        setSelectedId(match.id);
      } else {
        // Item no longer in the feed — clear the URL gracefully.
        intendedFocusRef.current = null;
        const p = new URLSearchParams(window.location.search);
        p.delete("focus");
        p.delete("highlight");
        const qs = p.toString();
        navigate(qs ? "?" + qs : window.location.pathname, { replace: true });
      }
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [radarEnabled, itemsLoaded, items, navigate]);

  // Optimistic-removal set: cardKeys restored in this session are hidden
  // immediately; on API failure they are re-added (rollback).
  const [restoredKeys, setRestoredKeys] = useState(new Set());

  const liveDismissed = (apiDismissed ?? []).filter(
    (c) => !restoredKeys.has(c.cardKey),
  );

  const acknowledgedCards = liveDismissed.filter(
    (c) => c.dismissedAction === "acknowledged",
  );
  const snoozedFromHome = liveDismissed.filter(
    (c) => c.dismissedAction === "snoozed",
  );

  async function handleRestoreFromHome(cardKey) {
    if (!isEditor) return;
    // Optimistic removal
    setRestoredKeys((prev) => new Set([...prev, cardKey]));
    try {
      await restoreInFocusCard(cardKey);
    } catch {
      // Roll back on failure
      setRestoredKeys((prev) => {
        const next = new Set(prev);
        next.delete(cardKey);
        return next;
      });
    }
  }

  // Delete confirm dialog state
  const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);

  // Toast state
  const [infocusToast, setInfocusToast] = useState({ visible: false, message: "", onUndo: null });
  const infocusToastTimer = useRef(null);

  const showInfocusToast = useCallback((message, onUndo = null) => {
    if (infocusToastTimer.current) clearTimeout(infocusToastTimer.current);
    setInfocusToast({ visible: true, message, onUndo });
    infocusToastTimer.current = setTimeout(
      () => setInfocusToast({ visible: false, message: "", onUndo: null }),
      6000,
    );
  }, []);

  // Resolve state — tracks the item being resolved so the drawer can disable actions
  const [resolving, setResolving] = useState(null);

  // handleResolve — optimistic removal + API call + undo toast
  const handleResolve = useCallback(async (itemId, verb, extraData = null) => {
    const item = (items ?? []).find((i) => i.id === itemId);
    if (!item) return;
    const dbId = item._dbId;

    setResolving(itemId);

    // Optimistic removal
    setItems((prev) => (prev ? prev.filter((i) => i.id !== itemId) : prev));
    handleCloseDrawer();

    try {
      await resolveFocusItem(dbId, verb, extraData);

      // Build a human-readable verb label for the toast
      const verbLabel = {
        log_decision:  "Decision logged",
        acknowledge:   "Acknowledged",
        mark_reviewed: "Marked reviewed",
        mark_fixed:    "Marked fixed",
        mark_resolved: "Resolved",
        mark_done:     "Marked done",
      }[verb] ?? "Resolved";

      // Undo is only offered for verbs that are pure state-flips on focus_items.
      // log_decision writes a portfolio_decisions record transactionally; /unresolve
      // cannot remove that record, so offering Undo would mislead the user.
      const REVERSIBLE_VERBS = new Set([
        "acknowledge", "mark_reviewed", "mark_fixed", "mark_resolved", "mark_done",
      ]);
      const undoCb = REVERSIBLE_VERBS.has(verb) ? async () => {
        try {
          await unresolveFocusItem(dbId);
          setReloadKey((n) => n + 1);
        } catch {
          showToast("Undo failed · please refresh", { type: "error" });
        }
      } : null;

      showInfocusToast(`${verbLabel} · removed from feed`, undoCb);
    } catch {
      // Roll back — re-insert item and re-open drawer
      setItems((prev) => (prev ? [...prev, item] : [item]));
      setSelectedId(itemId);
      showToast("Action failed · please try again", { type: "error" });
    } finally {
      setResolving(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, showInfocusToast]);

  // Local triage state — pin/mute are local-only; snooze hits the real API.
  const [triageOverrides, setTriageOverrides] = useState({});

  function getEffectiveTriage(item) {
    return triageOverrides[item.id] ?? item.triage;
  }

  // Triage handler — pin/mute are local-only; snooze calls the real API.
  async function handleTriage(itemId, action) {
    const foundItem = (items ?? []).find((i) => i.id === itemId);
    const base = triageOverrides[itemId] ?? foundItem?.triage ?? {};

    if (action === "pin") {
      setTriageOverrides((prev) => ({
        ...prev,
        [itemId]: { ...base, pinned: !base.pinned },
      }));
      return;
    }
    if (action === "mute") {
      // Real API: persist mute to DB
      hasMutatedMutedRef.current = true;
      const dbId = foundItem?._dbId ?? itemId;
      // Optimistically add to muted list and hide from ranked feed
      const itemSnapshot = foundItem ? { ...foundItem } : null;
      if (itemSnapshot) {
        setApiMutedItems((prev) => [...prev, itemSnapshot]);
      }
      setTriageOverrides((prev) => ({
        ...prev,
        [itemId]: { ...base, muted: true },
      }));
      if (selectedId === itemId) handleCloseDrawer();
      try {
        await muteFocusItem(dbId);
        showInfocusToast("Muted · item hidden from feed");
        // Reload feed so the item is properly excluded server-side
        setReloadKey((n) => n + 1);
      } catch {
        // Roll back on failure
        setApiMutedItems((prev) => prev.filter((i) => i.id !== itemId));
        setTriageOverrides((prev) => ({
          ...prev,
          [itemId]: { ...base, muted: false },
        }));
        showToast("Mute failed · please try again", { type: "error" });
      }
      return;
    }
    if (action === "unmute") {
      // Real API: clear mute, item re-appears in ranked feed
      hasMutatedMutedRef.current = true;
      const mutedEntry = apiMutedItems.find((i) => i.id === itemId);
      const dbId = mutedEntry?._dbId ?? itemId;
      // Optimistically remove from muted list and clear local triageOverride
      setApiMutedItems((prev) => prev.filter((i) => i.id !== itemId));
      setTriageOverrides((prev) => {
        const next = { ...prev };
        if (next[itemId]) {
          next[itemId] = { ...next[itemId], muted: false };
        }
        return next;
      });
      try {
        await unmuteFocusItem(dbId);
        showInfocusToast("Restored · item back in feed");
        // Reload feed to surface the newly-unmuted item from the server
        setReloadKey((n) => n + 1);
      } catch {
        // Roll back on failure
        if (mutedEntry) {
          setApiMutedItems((prev) => [...prev, mutedEntry]);
        }
        setTriageOverrides((prev) => ({
          ...prev,
          [itemId]: { ...base, muted: true },
        }));
        showToast("Restore failed · please try again", { type: "error" });
      }
      return;
    }
    if (action === "snooze") {
      // Real API: snooze for 7 days
      const dbId = foundItem?._dbId ?? itemId;
      const until = new Date();
      until.setDate(until.getDate() + 7);
      const snoozeUntil = until.toISOString();
      // Optimistically mark snoozed in local overrides
      setTriageOverrides((prev) => ({
        ...prev,
        [itemId]: { ...base, snoozedUntil: snoozeUntil },
      }));
      // Close drawer if this item is selected
      if (selectedId === itemId) {
        handleCloseDrawer();
      }
      try {
        await snoozeFocusItem(dbId, snoozeUntil);
        showInfocusToast("Snoozed · item hidden for 7 days");
      } catch {
        // Roll back on failure
        setTriageOverrides((prev) => ({
          ...prev,
          [itemId]: { ...base, snoozedUntil: null },
        }));
        showToast("Snooze failed · please try again", { type: "error" });
      }
      return;
    }
    if (action === "unsnooze") {
      // Real API: clear snooze, return item to ranked feed
      const dbId = foundItem?._dbId ?? itemId;
      // Optimistically clear snoozedUntil
      setTriageOverrides((prev) => ({
        ...prev,
        [itemId]: { ...base, snoozedUntil: null },
      }));
      try {
        await unsnoozeFocusItem(dbId);
        showInfocusToast("Restored · item back in feed");
      } catch {
        // Roll back on failure
        setTriageOverrides((prev) => ({
          ...prev,
          [itemId]: { ...base },
        }));
        showToast("Restore failed · please try again", { type: "error" });
      }
      return;
    }
  }

  // Open the delete confirm dialog for the given item id.
  function handleDelete(itemId) {
    const item = (items ?? []).find((i) => i.id === itemId) ?? null;
    setDeleteConfirmItem(item);
  }

  // Confirm: call the API, optimistically remove from local state.
  async function handleDeleteConfirm() {
    const item = deleteConfirmItem;
    if (!item) return;
    setDeleteConfirmItem(null);
    // Close drawer if the deleted item was open
    if (selectedId === item.id) {
      handleCloseDrawer();
    }
    // Optimistically remove from the item list
    setItems((prev) => (prev ? prev.filter((i) => i.id !== item.id) : prev));
    // Also remove from triage overrides
    setTriageOverrides((prev) => {
      const next = { ...prev };
      delete next[item.id];
      return next;
    });
    try {
      await dismissFocusItem(item._dbId);
      showInfocusToast("Dismissed · recoverable in Trash");
    } catch {
      // Roll back on failure — re-insert the item
      setItems((prev) => (prev ? [...prev, item] : prev));
    }
  }

  // INFOCUS-TRAY-URL-STATE: Close tray — clear ?focus / ?highlight, replace history entry.
  function handleCloseDrawer() {
    intendedFocusRef.current = null;
    setSelectedId(null);
    const p = new URLSearchParams(window.location.search);
    p.delete("focus");
    p.delete("highlight");
    const qs = p.toString();
    navigate(qs ? "?" + qs : window.location.pathname, { replace: true });
  }

  // INFOCUS-TRAY-URL-STATE: Select a row — push ?focus=<id> on fresh open so
  // Back restores the tray; replace when switching between items (no stacking).
  // Toggling the same row closed calls handleCloseDrawer.
  function handleRowSelect(item) {
    if (selectedId === item.id) {
      handleCloseDrawer();
      return;
    }
    const p = new URLSearchParams(window.location.search);
    const currentFocus = p.get("focus");
    intendedFocusRef.current = item.id;
    setSelectedId(item.id);
    p.set("focus", item.id);
    const qs = p.toString();
    if (currentFocus) {
      // Switching items — replace to avoid stacking history entries.
      navigate("?" + qs, { replace: true });
    } else {
      // Opening fresh — push so Back returns with the tray open.
      navigate("?" + qs);
    }
  }

  // Partition: pinned / session-snoozed / ranked
  // Note: muted items are excluded from the feed server-side (muted=FALSE in WHERE)
  // and tracked separately in apiMutedItems (fetched from GET /muted).
  const isVisibleItem = (item) => planEnabled || item.decisionSubType !== "plan-coverage-gap";
  const loadedItems = (items ?? []).filter(isVisibleItem);
  const pinnedItems = loadedItems.filter((i) => {
    const t = getEffectiveTriage(i);
    return t.pinned;
  });
  // Session-snoozed: items still in the feed but locally marked snoozed this session.
  // Also includes items optimistically muted this session (triageOverrides.muted=true)
  // until the feed reloads and excludes them server-side.
  const mutedItems = loadedItems.filter((i) => {
    const t = getEffectiveTriage(i);
    return !t.pinned && (t.muted || t.snoozedUntil);
  });
  const rankedItems = loadedItems.filter((i) => {
    const t = getEffectiveTriage(i);
    return !t.pinned && !t.muted && !t.snoozedUntil;
  });

  // Apply type filter, search, stat-filter, and active sort to ranked (not pinned)
  const activeComparator = sortPrimary.key === "priority" ? sortByUrgency : sortDueSoonest;
  const filteredRanked = useMemo(() => {
    const q = search;
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    return (typeFilter === "All"
      ? rankedItems
      : rankedItems.filter((i) => i.type === typeFilter)
    )
      .filter((i) => {
        if (!q) return true;
        return (
          matchesFoldedQuery(q, i.title, i.source.label)
        );
      })
      .filter((i) => {
        if (!statFilter) return true;
        if (statFilter === "in_focus")   return i.inFocus;
        if (statFilter === "act_now")    return i.band === "act_now";
        if (statFilter === "this_cycle") return i.band === "this_cycle";
        if (statFilter === "overdue") {
          const days = i.due ? parseInt(i.due.countdown, 10) : Infinity;
          return !isNaN(days) && days < 0;
        }
        if (statFilter === "unassigned") return i.owner === null;
        return true;
      })
      .slice()
      .sort(activeComparator);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankedItems, typeFilter, search, statFilter, activeComparator]);

  // Counts — total is all tracked items regardless of filter/triage
  const totalTracked  = loadedItems.length;
  const inFocusCount  = [...pinnedItems, ...filteredRanked].filter((i) => i.inFocus).length;
  // mutedCount: DB-persisted muted items (from apiMutedItems)
  const mutedCount   = apiMutedItems.length;
  // snoozedCount: items snoozed in this session via local triageOverrides
  const snoozedCount = mutedItems.filter((i) => {
    const t = getEffectiveTriage(i);
    return !!t.snoozedUntil;
  }).length;

  // Dismissed segment count — DB-muted + session-snoozed + API-sourced acknowledged/snoozed-from-home
  const dismissedCount = apiMutedItems.length + mutedItems.length + acknowledgedCards.length + snoozedFromHome.length;

  // KPI tile counts (portfolio-wide, unfiltered by statFilter/search).
  const kpiCounts = useMemo(() => {
    return {
      inFocus:   loadedItems.filter((i) => i.inFocus).length,
      actNow:    rankedItems.filter((i) => i.band === "act_now").length,
      thisCycle: rankedItems.filter((i) => i.band === "this_cycle").length,
      overdue:   loadedItems.filter((i) => {
        if (!i.due) return false;
        const days = parseInt(i.due.countdown, 10);
        return !isNaN(days) && days < 0;
      }).length,
      unassigned: loadedItems.filter((i) => i.owner === null).length,
      tracked:    loadedItems.length,
    };
  }, [loadedItems, rankedItems]);

  // Search active feed first, then DB-muted items so clicking a muted row
  // can still open its drawer ("click to review or restore").
  const selectedItem = selectedId
    ? (loadedItems.find((i) => i.id === selectedId) ?? apiMutedItems.find((i) => i.id === selectedId && isVisibleItem(i)) ?? null)
    : null;

  if (!radarEnabled) return null;

  return (
    <div data-testid="infocus-radar-page" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{
        maxWidth: PAGE_CONTENT_MAX_WIDTH,
        padding: PAGE_CONTENT_PADDING,
        margin: "0 auto",
        width: "100%",
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}>
      {/* KPI Tile Row — replaces the plain count line */}
      <div style={{ marginBottom: 14 }}>
        <KpiTileRow testId="kpi-tile-row-radar" columns={6}>
          <div onClick={() => setStatFilter((f) => f === "in_focus" ? null : "in_focus")} style={{ cursor: "pointer" }}>
            <KpiTile
              testId="kpi-tile-in-focus"
              label="In Focus"
              rawValue={kpiCounts.inFocus}
              color="var(--v2-accent)"
              sub="Top-ranked - needs your eyes"
              isFiltered={statFilter === "in_focus"}
            />
          </div>
          <div onClick={() => setStatFilter((f) => f === "act_now" ? null : "act_now")} style={{ cursor: "pointer" }}>
            <KpiTile
              testId="kpi-tile-act-now"
              label="Act Now"
              rawValue={kpiCounts.actNow}
              color="var(--v2-amber)"
              sub="Overdue or due ≤ 30 days"
              isFiltered={statFilter === "act_now"}
            />
          </div>
          <div onClick={() => setStatFilter((f) => f === "this_cycle" ? null : "this_cycle")} style={{ cursor: "pointer" }}>
            <KpiTile
              testId="kpi-tile-this-cycle"
              label="This Cycle"
              rawValue={kpiCounts.thisCycle}
              color="var(--v2-mid)"
              sub="Due within 90 days"
              isFiltered={statFilter === "this_cycle"}
            />
          </div>
          <div onClick={() => setStatFilter((f) => f === "overdue" ? null : "overdue")} style={{ cursor: "pointer" }}>
            <KpiTile
              testId="kpi-tile-overdue"
              label="Overdue"
              rawValue={kpiCounts.overdue}
              color="var(--v2-red)"
              sub="Past their due date"
              isFiltered={statFilter === "overdue"}
            />
          </div>
          <div onClick={() => setStatFilter((f) => f === "unassigned" ? null : "unassigned")} style={{ cursor: "pointer" }}>
            <KpiTile
              testId="kpi-tile-unassigned"
              label="Unassigned"
              rawValue={kpiCounts.unassigned}
              color="var(--v2-mid)"
              sub="No owner yet"
              isFiltered={statFilter === "unassigned"}
            />
          </div>
          <div onClick={() => setStatFilter(null)} style={{ cursor: "pointer" }}>
            <KpiTile
              testId="kpi-tile-tracked"
              label="Tracked"
              rawValue={kpiCounts.tracked}
              color="var(--v2-dark)"
              sub="Total items on Radar"
              isFiltered={statFilter !== null}
            />
          </div>
        </KpiTileRow>
      </div>

      {/* Toolbar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 12, padding: "10px 18px",
        flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Search */}
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search items…"
            testId="radar-search-input"
          />

          {/* Sort — SortControl with Priority / Due soonest fields */}
          <SortControl
            testId="radar-sort"
            fields={[
              { value: "due_soonest", label: "Due soonest" },
              { value: "priority",    label: "Priority"    },
            ]}
            primary={sortPrimary}
            secondary={sortSecondary}
            defaultKey="due_soonest"
            onChange={({ primary, secondary }) => {
              setSortPrimary(primary);
              setSortSecondary(secondary);
            }}
          />

          {/* Filter — dropdown listing all type options + Dismissed */}
          <div ref={filterRef} style={{ position: "relative" }}>
            <button
              data-testid="radar-filter-btn"
              onClick={() => setFilterOpen((o) => !o)}
              style={{
                border: typeFilter !== "All"
                  ? "1px solid var(--v2-accent)"
                  : "1px solid var(--v2-line)",
                background: typeFilter !== "All"
                  ? "var(--v2-blue-tint)"
                  : "var(--v2-card)",
                color: typeFilter !== "All"
                  ? "var(--v2-accent)"
                  : "var(--v2-dark)",
                borderRadius: 8, padding: "6px 11px",
                fontSize: T.fsSm5, fontWeight: T.fwSemibold,
                cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
              }}
            >
              {typeFilter === "All" ? "Filter" : `Filter · ${typeFilter}`}
              <ChevronDown size={13} />
            </button>

            {filterOpen && (
              <div
                style={{
                  position: "absolute", top: "calc(100% + 6px)", left: 0,
                  zIndex: 30, minWidth: 160,
                  background: "var(--v2-card)",
                  border: "1px solid var(--v2-line)",
                  borderRadius: 10,
                  boxShadow: "0 4px 18px rgba(16,36,58,.13)",
                  padding: 4,
                }}
              >
                {TYPE_OPTS.map((opt) => {
                  const isActive = typeFilter === opt;
                  const label = opt === "Dismissed" && dismissedCount > 0
                    ? `Dismissed (${dismissedCount})`
                    : opt;
                  return (
                    <button
                      key={opt}
                      data-testid={`radar-type-${opt.toLowerCase()}`}
                      onClick={() => { setTypeFilter(opt); setFilterOpen(false); }}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        width: "100%", textAlign: "left",
                        border: 0, background: isActive ? "var(--v2-blue-tint)" : "transparent",
                        color: isActive ? "var(--v2-accent)" : "var(--v2-dark)",
                        borderRadius: 7, padding: "6px 12px",
                        fontSize: T.fsSm5, fontWeight: isActive ? T.fwBold : T.fwSemibold,
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) e.currentTarget.style.background = "var(--v2-tint)";
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) e.currentTarget.style.background = "transparent";
                      }}
                    >
                      {label}
                      {isActive && (
                        <span style={{ fontSize: T.fsXs, color: "var(--v2-accent)" }}>✓</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {/* Compact density switch */}
          <CompactSwitch checked={compact} onChange={toggleCompact} testId="radar-compact-switch" />

          {/* Updated timestamp */}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span
              data-testid="radar-refresh-timestamp"
              style={{ fontSize: T.fsSm, color: "var(--v2-light)" }}
            >
              Updated {ageLabel}
            </span>
            {isCooldown && !refreshing && (
              <span
                data-testid="radar-current-pill"
                style={{
                  padding: "1px 8px",
                  borderRadius: 10,
                  background: T.utilGreen,
                  color: T.dark,
                  fontWeight: T.fwSemibold,
                  fontSize: T.fsXxs,
                }}
              >
                ✓ Current
              </span>
            )}
          </span>

          {/* Refresh */}
          <button
            type="button"
            data-testid="radar-refresh-btn"
            onClick={handleRefresh}
            disabled={isDisabled}
            onMouseEnter={(e) => {
              if (!isDisabled) {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(16,36,58,.18)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "";
              e.currentTarget.style.boxShadow = "";
            }}
            style={{
              border: "none",
              borderRadius: 8,
              padding: "6px 14px",
              fontSize: T.fsSm5,
              fontWeight: T.fwBold,
              background: isDisabled ? T.lightGray : T.blue,
              color: isDisabled ? T.mid : T.white,
              cursor: isDisabled ? "default" : "pointer",
              opacity: isDisabled ? 0.7 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              transition: "transform .15s, box-shadow .15s",
            }}
          >
            ↻ {refreshing ? "Refreshing…" : isCooldown ? "Current" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr", overflow: "hidden" }}>
        {/* Ledger */}
        <div ref={setLedgerElement} style={{ overflowY: "auto" }}>
          {/* Column headers */}
          <div role="row" style={{
            display: "grid", gridTemplateColumns: GRID_COLS,
            minWidth: 800,
            alignItems: "center", gap: 12, padding: "8px 18px",
            background: "var(--v2-tint)",
            borderBottom: "1px solid var(--v2-line)",
            position: "sticky", top: 0, zIndex: 10,
            borderRadius: "10px 10px 0 0",
          }}>
            {["Type", "Item", "Owner", "Due", "Urgency", ""].map((h, i) => (
              <div key={i} style={{
                fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".05em",
                textTransform: "uppercase", color: "var(--v2-light)",
                textAlign: h === "Urgency" ? "right" : "left",
              }}>
                {h}
              </div>
            ))}
          </div>
          {ledgerOverflow.header && createPortal(<OverflowButton overflow={ledgerOverflow.overflow} right={ledgerOverflow.right} />, ledgerOverflow.header)}

          {typeFilter === "Dismissed" ? (
            /* ── Dismissed view ── */
            <div data-testid="radar-dismissed-body">
              {dismissedCount === 0 ? (
                <div style={{ padding: "32px 18px", color: "var(--v2-light)", fontSize: T.fsSm }}>
                  Nothing dismissed yet.
                </div>
              ) : (
                <>
                  {/* DB-muted items (persisted via API) */}
                  {apiMutedItems.length > 0 && (
                    <>
                      <SectionLabel>Muted</SectionLabel>
                      {apiMutedItems.map((item) => (
                        <LedgerRow
                          key={item.id}
                          item={item}
                          isSelected={selectedId === item.id}
                          onSelect={handleRowSelect}
                          triage={getEffectiveTriage(item)}
                          onTriage={handleTriage}
                          onDelete={handleDelete}
                          isEditor={isEditor}
                          restoreAction={isEditor ? "unmute" : undefined}
                        />
                      ))}
                    </>
                  )}

                  {/* Local snoozed items */}
                  {mutedItems.filter((i) => !!getEffectiveTriage(i).snoozedUntil).length > 0 && (
                    <>
                      <SectionLabel>Snoozed</SectionLabel>
                      {mutedItems.filter((i) => !!getEffectiveTriage(i).snoozedUntil).map((item) => (
                        <LedgerRow
                          key={item.id}
                          item={item}
                          isSelected={selectedId === item.id}
                          onSelect={handleRowSelect}
                          triage={getEffectiveTriage(item)}
                          onTriage={handleTriage}
                          onDelete={handleDelete}
                          isEditor={isEditor}
                          restoreAction="unsnooze"
                        />
                      ))}
                    </>
                  )}

                  {/* API-sourced dismissed / snoozed-from-home groups */}
                  {(acknowledgedCards.length > 0 || snoozedFromHome.length > 0) && (
                    <div style={{ padding: "10px 18px 14px" }}>
                      <DismissedGroup
                        testId="infocus-radar-dismissed-section"
                        label="Dismissed"
                        subtitle="Hidden from In Focus · Restore to bring back"
                        cards={acknowledgedCards}
                        onRestore={handleRestoreFromHome}
                        currentRole={currentRole}
                      />
                      <DismissedGroup
                        testId="infocus-radar-snoozed-section"
                        label="Snoozed"
                        subtitle="Returns on scheduled date"
                        cards={snoozedFromHome}
                        onRestore={handleRestoreFromHome}
                        currentRole={currentRole}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <>
              {/* Pinned section */}
              {pinnedItems.length > 0 && (
                <div style={{
                  background: "var(--v2-card)", borderRadius: 10,
                  border: "1px solid var(--v2-line)", marginBottom: 8, overflow: "hidden",
                }}>
                  <div style={{
                    fontSize: T.fsXxs, fontWeight: T.fwBold, textTransform: "uppercase",
                    letterSpacing: ".06em", color: "var(--v2-light)",
                    padding: "9px 18px 5px",
                    position: "sticky", top: 0, zIndex: 2,
                    background: "var(--v2-tint)",
                    borderRadius: "10px 10px 0 0",
                  }}>
                    Pinned
                  </div>
                  {pinnedItems.map((item) => (
                    <LedgerRow
                      key={item.id}
                      item={item}
                      isSelected={selectedId === item.id}
                      onSelect={handleRowSelect}
                      triage={getEffectiveTriage(item)}
                      onTriage={handleTriage}
                      onDelete={handleDelete}
                      isEditor={isEditor}
                      compact={compact}
                    />
                  ))}
                </div>
              )}

              {/* Band-grouped ranked section */}
              {filteredRanked.length === 0 ? (
                <div style={{ padding: "24px 18px", color: "var(--v2-light)", fontSize: T.fsSm }}>
                  No items match the selected filter.
                </div>
              ) : BANDS.map((bandCfg) => {
                const bandItems = filteredRanked.filter(
                  (i) => (i.band ?? "keep_watch") === bandCfg.key,
                );
                if (bandItems.length === 0) return null;
                return (
                  <div key={bandCfg.key} style={{ marginBottom: 16 }}>
                    {/* Pill header floats above the rounded card */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "2px 4px 8px" }}>
                      <span style={{
                        fontSize: T.fsXs, fontWeight: T.fwBold,
                        padding: "3px 10px", borderRadius: 7,
                        background: bandCfg.pillBg, color: bandCfg.pillColor,
                        whiteSpace: "nowrap",
                      }}>
                        {bandCfg.label}
                      </span>
                      <span style={{ fontSize: T.fsSm, color: "var(--v2-mid)" }}>
                        {bandCfg.desc}
                      </span>
                      <span style={{ marginLeft: "auto", fontSize: T.fsSm, fontWeight: T.fwBold, color: "var(--v2-mid)" }}>
                        {bandItems.length} {bandItems.length === 1 ? "item" : "items"}
                      </span>
                    </div>
                    {/* Rounded card — no internal strip header */}
                    <div style={{
                      background: "var(--v2-card)", borderRadius: 10,
                      border: "1px solid var(--v2-line)", overflow: "clip", minWidth: 800,
                    }}>
                      {bandItems.map((item) => (
                        <LedgerRow
                          key={item.id}
                          item={item}
                          isSelected={selectedId === item.id}
                          onSelect={handleRowSelect}
                          triage={getEffectiveTriage(item)}
                          onTriage={handleTriage}
                          onDelete={handleDelete}
                          isEditor={isEditor}
                          compact={compact}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Muted / Snoozed strip — hidden when Dismissed segment is active */}
              <button
                data-testid="radar-muted-strip"
                onClick={() => setMutedOpen((v) => !v)}
                style={{
                  width: "100%", textAlign: "left",
                  padding: "9px 18px",
                  background: "var(--v2-tint)",
                  border: "none",
                  borderTop: "1px solid var(--v2-line)",
                  fontSize: T.fsSm, color: "var(--v2-mid)",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 7,
                }}
              >
                <MoreHorizontal size={14} />
                <b style={{ color: "var(--v2-dark)" }}>Muted ({mutedCount})</b>
                {" · "}
                <b style={{ color: "var(--v2-dark)" }}>Snoozed ({snoozedCount})</b>
                {"- hidden from In focus; click to review or restore"}
              </button>
              {mutedOpen && (
                <>
                  {/* DB-muted items — Restore calls unmuteFocusItem (editor only) */}
                  {apiMutedItems.map((item) => (
                    <LedgerRow
                      key={item.id}
                      item={item}
                      isSelected={selectedId === item.id}
                      onSelect={handleRowSelect}
                      triage={getEffectiveTriage(item)}
                      onTriage={handleTriage}
                      onDelete={handleDelete}
                      isEditor={isEditor}
                      restoreAction={isEditor ? "unmute" : undefined}
                    />
                  ))}
                  {/* Session-snoozed items — Restore calls unsnoozeFocusItem */}
                  {mutedItems.filter((i) => !!getEffectiveTriage(i).snoozedUntil).map((item) => (
                    <LedgerRow
                      key={item.id}
                      item={item}
                      isSelected={selectedId === item.id}
                      onSelect={handleRowSelect}
                      triage={getEffectiveTriage(item)}
                      onTriage={handleTriage}
                      onDelete={handleDelete}
                      isEditor={isEditor}
                      restoreAction="unsnooze"
                    />
                  ))}
                </>
              )}

              {/* Dismissed / Snoozed groups — real dismissed cards from Pulse home */}
              {(acknowledgedCards.length > 0 || snoozedFromHome.length > 0) && (
                <div style={{ borderTop: "1px solid var(--v2-line)", padding: "10px 18px 14px" }}>
                  <DismissedGroup
                    testId="infocus-radar-dismissed-section"
                    label="Dismissed"
                    subtitle="Hidden from In Focus · Restore to bring back"
                    cards={acknowledgedCards}
                    onRestore={handleRestoreFromHome}
                    currentRole={currentRole}
                  />
                  <DismissedGroup
                    testId="infocus-radar-snoozed-section"
                    label="Snoozed"
                    subtitle="Returns on scheduled date"
                    cards={snoozedFromHome}
                    onRestore={handleRestoreFromHome}
                    currentRole={currentRole}
                  />
                </div>
              )}
            </>
          )}
        </div>

      </div>

      {decisionsRegisterEnabled && (
        <RadarDecisionsPanel currentRole={currentRole} />
      )}

      </div>{/* end constrained wrapper */}

      {/* Drawer */}
      <SlideSheet
        open={!!selectedItem}
        onClose={handleCloseDrawer}
        width={420}
        panelTestId="radar-drawer"
        scrimTestId="radar-drawer-scrim"
      >
        {selectedItem && (
          <DrawerPanel
            item={selectedItem}
            triage={getEffectiveTriage(selectedItem)}
            onTriage={handleTriage}
            onDelete={handleDelete}
            isEditor={isEditor}
            onClose={handleCloseDrawer}
            onResolve={handleResolve}
            resolving={resolving}
          />
        )}
      </SlideSheet>

      {/* Delete confirm dialog */}
      {deleteConfirmItem && (
        <DeleteConfirmDialog
          item={deleteConfirmItem}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteConfirmItem(null)}
        />
      )}

      {/* Infocus toast */}
      <div
        data-testid="infocus-toast"
        style={{
          position: "fixed", bottom: 22, left: "50%",
          transform: `translateX(-50%) translateY(${infocusToast.visible ? 0 : 14}px)`,
          background: T.tooltip.bg, color: T.white,
          padding: "10px 18px", borderRadius: 10,
          fontSize: T.fsSm5, fontWeight: T.fwSemibold,
          opacity: infocusToast.visible ? 1 : 0,
          transition: "opacity 0.22s, transform 0.22s",
          pointerEvents: infocusToast.visible ? "auto" : "none",
          boxShadow: "0 8px 26px rgba(15,23,42,.28)", zIndex: LAYERS.toast,
          display: "flex", alignItems: "center", gap: 10,
        }}
      >
        <span>{infocusToast.message}</span>
        {infocusToast.onUndo && (
          <button
            data-testid="infocus-toast-undo"
            onClick={() => {
              setInfocusToast({ visible: false, message: "", onUndo: null });
              infocusToast.onUndo();
            }}
            style={{
              background: "transparent", border: "1px solid rgba(255,255,255,.45)",
              color: T.white, borderRadius: 6, padding: "3px 9px",
              fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Undo
          </button>
        )}
      </div>
    </div>
  );
}
